import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getContacts,
  getPending,
  sendRequest,
  respondToRequest,
  removeContact,
} from '../services/connectionService';
import { createOrFindDM } from '../services/dmService';
import {
  lookupUsers,
  getAddressBook,
  getBlockedUsers,
  blockUser,
  unblockUser,
} from '../services/userService';
import './ContactsPage.css';

function initials(name) {
  return (name || '?')[0].toUpperCase();
}

export default function ContactsPage() {
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addressBook, setAddressBook] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);

  // Search / add new contact
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [requestingId, setRequestingId] = useState(null);
  const [blockingId, setBlockingId] = useState(null);

  // Message button loading
  const [messagingId, setMessagingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, pendingRes, addressBookRes, blockedRes] = await Promise.all([
        getContacts(),
        getPending(),
        getAddressBook(),
        getBlockedUsers(),
      ]);
      setContacts(contactsRes.data.contacts || []);
      setIncoming(pendingRes.data.incoming || []);
      setOutgoing(pendingRes.data.outgoing || []);
      setAddressBook(addressBookRes.data.entries || []);
      setBlockedUsers(blockedRes.data.blocked || []);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter search results — exclude existing contacts and pending
  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    const contactUserIds = new Set(contacts.map((c) => String(c.user._id)));
    const outgoingIds = new Set(outgoing.map((o) => String(o.recipient._id)));
    const incomingIds = new Set(incoming.map((i) => String(i.requester._id)));

    let ignore = false;
    lookupUsers(query)
      .then((res) => {
        if (ignore) return;
        const results = (res.data.users || []).filter(
          (u) =>
            !contactUserIds.has(String(u._id)) &&
            !outgoingIds.has(String(u._id)) &&
            !incomingIds.has(String(u._id))
        );
        setSearchResults(results);
      })
      .catch(() => {
        if (!ignore) setSearchResults([]);
      });
    return () => {
      ignore = true;
    };
  }, [searchQuery, contacts, outgoing, incoming]);

  const handleBlock = async (userId) => {
    setBlockingId(String(userId));
    try {
      await blockUser(userId);
      await load();
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to block user');
    } finally {
      setBlockingId(null);
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await unblockUser(userId);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unblock user');
    }
  };

  const handleSendRequest = async (userId) => {
    setRequestingId(userId);
    try {
      await sendRequest(userId);
      await load();
      setSearchQuery('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to send request');
    } finally {
      setRequestingId(null);
    }
  };

  const handleRespond = async (connectionId, status) => {
    try {
      await respondToRequest(connectionId, status);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to respond');
    }
  };

  const handleRemove = async (connectionId) => {
    if (!window.confirm('Remove this contact?')) return;
    try {
      await removeContact(connectionId);
      setContacts((prev) => prev.filter((c) => String(c.connectionId) !== String(connectionId)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove');
    }
  };

  const handleMessage = async (contact) => {
    setMessagingId(String(contact.user._id));
    try {
      const res = await createOrFindDM({ recipientId: String(contact.user._id) });
      navigate('/messages', { state: { activeThreadId: res.data.thread._id } });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to open messages');
    } finally {
      setMessagingId(null);
    }
  };

  const renderContactActions = (entry, connectionId) => {
    const userId = String(entry._id);
    const contactUserIds = new Set(contacts.map((contact) => String(contact.user._id)));
    const outgoingIds = new Set(outgoing.map((item) => String(item.recipient._id)));
    const incomingIds = new Set(incoming.map((item) => String(item.requester._id)));
    const isContact = contactUserIds.has(userId);
    const isPendingOutgoing = outgoingIds.has(userId);
    const isPendingIncoming = incomingIds.has(userId);

    return (
      <div className="contact-actions">
        {isContact ? (
          <button
            className="btn btn-primary btn-sm"
            disabled={messagingId === userId}
            onClick={() => handleMessage({ user: entry })}
          >
            {messagingId === userId ? '…' : '✉ Message'}
          </button>
        ) : isPendingOutgoing ? (
          <span className="contact-pending-badge">Pending</span>
        ) : isPendingIncoming ? null : (
          <button
            className="btn btn-primary btn-sm"
            disabled={requestingId === userId}
            onClick={() => handleSendRequest(userId)}
          >
            {requestingId === userId ? 'Sending…' : '+ Connect'}
          </button>
        )}
        {!connectionId && (
          <button
            className="btn btn-ghost btn-sm contact-remove-btn"
            disabled={blockingId === userId}
            onClick={() => handleBlock(userId)}
            title="Block user"
          >
            {blockingId === userId ? '…' : 'Block'}
          </button>
        )}
        {connectionId && (
          <>
            <button
              className="btn btn-ghost btn-sm contact-remove-btn"
              title="Remove contact"
              onClick={() => handleRemove(connectionId)}
            >
              ✕
            </button>
            <button
              className="btn btn-ghost btn-sm contact-remove-btn"
              disabled={blockingId === userId}
              onClick={() => handleBlock(userId)}
              title="Block user"
            >
              {blockingId === userId ? '…' : 'Block'}
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="contacts-page">
      <div className="contacts-header">
        <h1 className="contacts-title">Contacts</h1>
        <p className="contacts-subtitle">Connect with people to send direct messages</p>
      </div>

      {/* Search / Add */}
      <div className="contacts-search-section">
        <div className="contacts-search-wrap">
          <input
            className="contacts-search-input"
            placeholder="Type a user name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="contacts-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
        {searchResults.length > 0 && (
          <ul className="contacts-search-results">
            {searchResults.map((u) => (
              <li key={u._id} className="contacts-search-item">
                <div className="contact-avatar">{initials(u.name)}</div>
                <div className="contact-info">
                  <span className="contact-name">{u.name}</span>
                  <span className="contact-email">{u.email}</span>
                </div>
                {renderContactActions(u)}
              </li>
            ))}
          </ul>
        )}
        {searchQuery && searchResults.length === 0 && !loading && (
          <p className="contacts-search-empty">No users found matching "{searchQuery}"</p>
        )}
      </div>

      {loading ? (
        <p className="contacts-muted">Loading...</p>
      ) : (
        <>
          <div className="contacts-section">
            <h2 className="contacts-section-heading">Address Book ({addressBook.length})</h2>
            {addressBook.length === 0 ? (
              <p className="contacts-muted">Used recipients will appear here automatically.</p>
            ) : (
              <ul className="contacts-list">
                {addressBook.map((entry) => (
                  <li key={String(entry.user._id)} className="contact-item">
                    <div className="contact-avatar">{initials(entry.user?.name)}</div>
                    <div className="contact-info">
                      <span className="contact-name">{entry.user?.name}</span>
                      <span className="contact-email">{entry.user?.email}</span>
                    </div>
                    {renderContactActions(entry.user)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Incoming requests */}
          {incoming.length > 0 && (
            <div className="contacts-section">
              <h2 className="contacts-section-heading">
                Pending Requests
                <span className="contacts-badge">{incoming.length}</span>
              </h2>
              <ul className="contacts-list">
                {incoming.map((req) => (
                  <li key={req._id} className="contact-item">
                    <div className="contact-avatar">{initials(req.requester?.name)}</div>
                    <div className="contact-info">
                      <span className="contact-name">{req.requester?.name}</span>
                      <span className="contact-email">{req.requester?.email}</span>
                    </div>
                    <div className="contact-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleRespond(req._id, 'accepted')}
                      >
                        Accept
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleRespond(req._id, 'declined')}
                      >
                        Decline
                      </button>
                      <button
                        className="btn btn-ghost btn-sm contact-remove-btn"
                        disabled={blockingId === String(req.requester?._id)}
                        onClick={() => handleBlock(String(req.requester?._id))}
                      >
                        {blockingId === String(req.requester?._id) ? '…' : 'Block'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Outgoing (sent) */}
          {outgoing.length > 0 && (
            <div className="contacts-section">
              <h2 className="contacts-section-heading">Sent Requests</h2>
              <ul className="contacts-list">
                {outgoing.map((req) => (
                  <li key={req._id} className="contact-item">
                    <div className="contact-avatar">{initials(req.recipient?.name)}</div>
                    <div className="contact-info">
                      <span className="contact-name">{req.recipient?.name}</span>
                      <span className="contact-email">{req.recipient?.email}</span>
                    </div>
                    <div className="contact-actions">
                      <span className="contact-pending-badge">Pending</span>
                      <button
                        className="btn btn-ghost btn-sm contact-remove-btn"
                        disabled={blockingId === String(req.recipient?._id)}
                        onClick={() => handleBlock(String(req.recipient?._id))}
                      >
                        {blockingId === String(req.recipient?._id) ? '…' : 'Block'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contacts */}
          <div className="contacts-section">
            <h2 className="contacts-section-heading">My Contacts ({contacts.length})</h2>
            {contacts.length === 0 ? (
              <p className="contacts-muted">No contacts yet. Search for people above to connect.</p>
            ) : (
              <ul className="contacts-list">
                {contacts.map((c) => (
                  <li key={String(c.connectionId)} className="contact-item">
                    <div className="contact-avatar">{initials(c.user?.name)}</div>
                    <div className="contact-info">
                      <span className="contact-name">{c.user?.name}</span>
                      <span className="contact-email">{c.user?.email}</span>
                    </div>
                    {renderContactActions(c.user, c.connectionId)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="contacts-section">
            <h2 className="contacts-section-heading">Blocked Users ({blockedUsers.length})</h2>
            {blockedUsers.length === 0 ? (
              <p className="contacts-muted">No blocked users.</p>
            ) : (
              <ul className="contacts-list">
                {blockedUsers.map((blocked) => (
                  <li key={String(blocked._id)} className="contact-item">
                    <div className="contact-avatar">{initials(blocked.name)}</div>
                    <div className="contact-info">
                      <span className="contact-name">{blocked.name}</span>
                      <span className="contact-email">{blocked.email}</span>
                    </div>
                    <div className="contact-actions">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleUnblock(String(blocked._id))}
                      >
                        Unblock
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
